import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pwd, setPwd] = useState({ new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }
    if (pwd.new_password !== pwd.confirm) { toast.error("Las contraseñas no coinciden"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd.new_password });
      toast.success("Contraseña actualizada. Ya puedes iniciar sesión.");
      navigate("/login");
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#373E45] p-12">
        <Link to="/" data-testid="reset-logo-dark-link" className="self-start"><Logo variant="dark" className="h-9" /></Link>
        <div>
          <h2 className="text-3xl font-bold text-white leading-tight">Crea tu nueva<br />contraseña.</h2>
          <p className="mt-4 text-slate-300 max-w-md">Elige una contraseña segura para tu clínica.</p>
        </div>
        <p className="text-slate-400 text-sm">© {new Date().getFullYear()} Veteo</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Link to="/" data-testid="reset-logo-link"><Logo className="h-10" /></Link></div>
          <h1 className="text-2xl font-extrabold text-[#373E45]">Nueva contraseña</h1>
          {!token ? (
            <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-2" data-testid="reset-no-token">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">Este enlace no es válido. Solicita uno nuevo desde <Link to="/forgot-password" className="font-bold underline">¿Olvidaste tu contraseña?</Link></p>
            </div>
          ) : (
            <>
              <p className="text-[#64748B] text-sm mt-1 mb-8">Introduce tu nueva contraseña.</p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <Label className="text-[#373E45] font-semibold">Nueva contraseña</Label>
                  <Input type="password" required minLength={6} value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                    className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="Mínimo 6 caracteres" data-testid="reset-new-password-input" />
                </div>
                <div>
                  <Label className="text-[#373E45] font-semibold">Repite la contraseña</Label>
                  <Input type="password" required value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                    className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="••••••••" data-testid="reset-confirm-password-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold rounded-xl h-11" data-testid="reset-submit-btn">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Guardar contraseña"}
                </Button>
              </form>
            </>
          )}
          <p className="text-sm text-[#64748B] mt-6 text-center">
            <Link to="/login" className="text-[#373E45] font-bold hover:underline" data-testid="goto-login-link">Volver a iniciar sesión</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
